import { Location, Money, Tags, Team } from '@clearkit/icons';
import { uniq } from 'lodash';

import { FilterMultiPicker } from '~/components/Filters/FilterMultiPicker';
import { useProspectFiltersQuery } from '~/generated/graphql';

import { Search } from '../hooks/useTargetMarket';
import {
  BUSINESS_MODELS,
  COMPANY_TYPES,
  COUNTRIES,
  EMPLOYEE_RANGES,
  INDUSTRIES,
  RAISED_RANGES,
  TECH,
} from './attributeOptions';

export const TargetMarketForm = ({
  search,
  setSearch,
}: {
  search: Search;
  setSearch: any;
}) => {
  const { data } = useProspectFiltersQuery();

  const tags = search?.tag ?? [];
  const businessModelValues = BUSINESS_MODELS.map((tag) => tag.value);
  const businessModelTags = tags.filter((tag) =>
    businessModelValues.includes(tag),
  );
  const companyTags = tags.filter((tag) => !businessModelValues.includes(tag));

  const updateSearch = (fieldName: keyof Search) => (value: string[]) => {
    const newSearch = {
      ...search,
      [fieldName]: value,
    };

    setSearch(newSearch);
  };

  const updateBusinessModel = (value: string[]) => {
    const allTags = [...companyTags, ...value];
    setSearch({
      ...search,
      tag: uniq(allTags),
    });
  };

  const updateTags = (value: string[]) => {
    const allTags = [...businessModelTags, ...value];
    setSearch({
      ...search,
      tag: uniq(allTags),
    });
  };

  return (
    <div className="p-6 pt-0 divide-y space-y-4 divide-gray-200">
      <FilterMultiPicker
        items={EMPLOYEE_RANGES}
        onChange={updateSearch('employees')}
        value={search?.employees ?? []}
      >
        <FilterMultiPicker.Icon>
          <Team />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Employee Ranges</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={INDUSTRIES}
        onChange={updateSearch('industry')}
        value={search?.industry ?? []}
      >
        <FilterMultiPicker.Icon>
          <Tags />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Industries</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={COUNTRIES}
        onChange={updateSearch('country')}
        value={search?.country ?? []}
      >
        <FilterMultiPicker.Icon>
          <Location />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Countries</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={BUSINESS_MODELS}
        onChange={updateBusinessModel}
        value={businessModelTags}
      >
        <FilterMultiPicker.Icon>
          <Tags />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Business Models</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={COMPANY_TYPES}
        onChange={updateSearch('type')}
        value={search.type ?? []}
      >
        <FilterMultiPicker.Icon>
          <Tags />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Company Types</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={data?.prospectFilters?.companyTags ?? []}
        onChange={updateTags}
        value={companyTags}
      >
        <FilterMultiPicker.Icon>
          <Tags />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Tags</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={TECH}
        onChange={updateSearch('tech')}
        value={search?.tech ?? []}
      >
        <FilterMultiPicker.Icon>
          <Tags />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Technologies</FilterMultiPicker.Title>
      </FilterMultiPicker>

      <FilterMultiPicker
        className="pt-4"
        items={RAISED_RANGES}
        onChange={updateSearch('raised')}
        value={search?.raised ?? []}
      >
        <FilterMultiPicker.Icon>
          <Money />
        </FilterMultiPicker.Icon>
        <FilterMultiPicker.Title>Funding Raised</FilterMultiPicker.Title>
      </FilterMultiPicker>
    </div>
  );
};
